const {readCookie, TRIP_COOKIE} = require('../../universal/utils/cookieHelpers');
const {
    isValidDate,
    areBothDatesValid,
    isBefore,
    isPast,
    formatDate
} = require('./dateHelper');
import {getQueryParams} from '../utils/queryStringHelper';
import {log as logToServer} from '../api/logger';

// Return a value is it is true or false, but not if it is undefined
function isTrueOrFalse(val) {
    return (val === 'true' || val === 'false' || val === true || val === false);
}

// Why a dictionary instead of an array?  Because O(1) < 0(n)
const queryParamKeys = {
    locale: true,
    siteName: true,
    site: true,
    currency: true,
    r: true,
    cid: true,
    arrival: true,
    arrivalDate: true,
    arrive: true,
    arriveDate: true,
    departure: true,
    departureDate: true,
    depart: true,
    departDate: true,
    numberOfAdults: true,
    adultsCount: true,
    adults: true,
    numberOfChildren: true,
    childrenCount: true,
    children: true,
    numberOfInfants: true,
    infantsCount: true,
    infants: true,
    petIncluded: true,
    petsIncluded: true,
    pet: true,
    pets: true,
    noDates: true,
    noGuests: true,
    guests: true
};

/* istanbul ignore next */
function logUnrecognizedParams(query, logFn) {
    Object.keys(query).forEach((key) => {
        if (!queryParamKeys[key]) {
            const value = query[key];
            logFn({
                queryParam: key,
                used: false,
                processed: false,
                value
            });
        }
    });
}

/* istanbul ignore next */
function getValidQueryField(query, fieldData, logFn, validFn) {
    fieldData.all.forEach((key) => {
        const nextValue = query[key];
        if (!fieldData.used && (typeof nextValue !== 'undefined')) {
            const valid = validFn(nextValue);
            if (valid) {
                fieldData.used = key;
            } else {
                fieldData.invalid.push(key);
            }
        } else if (fieldData.used && (typeof nextValue !== 'undefined')) {
            fieldData.notUsed.push(key);
        }
    });
    if (logFn) {
        if (fieldData.used) {
            const value = query[fieldData.used];
            logFn({
                queryParam: fieldData.used,
                used: true,
                valid: true,
                value
            });
        }
        fieldData.invalid.forEach((key) => {
            logFn({
                queryParam: key,
                used: false,
                valid: false,
                value: query[key]
            });
        });
        fieldData.notUsed.forEach((key) => {
            logFn({
                queryParam: key,
                used: false,
                valid: 'unknown',
                value: query[key]
            });
        });
    }

    return query[fieldData.used];
}

function getArrivalQuery(query, logFn) {
    const arrival = {
        used: null,
        notUsed: [],
        invalid: [],
        all: ['arrival', 'arrivalDate', 'arrive', 'arriveDate']
    };

    return getValidQueryField(query, arrival, logFn, (value) => {
        return isValidDate(new Date(value));
    });
}

function getDepartureQuery(query, logFn) {
    const departure = {
        used: null,
        notUsed: [],
        invalid: [],
        all: ['departure', 'departureDate', 'depart', 'departDate']
    };

    return getValidQueryField(query, departure, logFn, (value) => {
        return isValidDate(new Date(value));
    });
}

function bothDatesExist(arriveDate, departDate) {
    return !!arriveDate && !!departDate;
}

function qualifyDates(arriveDate, departDate) {
    if (!bothDatesExist(arriveDate, departDate)) {
        return null;
    }
    if (!areBothDatesValid(arriveDate, departDate)) {
        return null;
    }
    if (!isBefore(arriveDate, departDate) || isPast(arriveDate)) {
        return {
            arriveDate: '',
            departDate: ''
        };
    }
    return {
        arriveDate: formatDate(arriveDate),
        departDate: formatDate(departDate)
    };
}

/* eslint-disable complexity */
function determineTripDates(query, tripCookie, stateCookie, logFn) {
    logUnrecognizedParams(query, logFn);
    const qArrival = getArrivalQuery(query, logFn);
    const qDeparture = getDepartureQuery(query, logFn);
    const datesFromQuery = qualifyDates(qArrival, qDeparture);
    const emptyDates = {
        source: 'none', // Used for debugging in store.js
        arriveDate: '',
        departDate: ''
    };

    if (query.noDates === 'true') {
        return emptyDates;
    }

    if (datesFromQuery) {
        datesFromQuery.source = 'queryParam'; // Used for debugging in store.js
        return datesFromQuery;
    }

    let datesFromTripCookie = null;
    if (tripCookie.arrival && tripCookie.departure) {
        const tArrival = tripCookie.arrival;
        const tDeparture = tripCookie.departure;
        datesFromTripCookie = qualifyDates(tArrival, tDeparture);
    }

    if (datesFromTripCookie) {
        datesFromTripCookie.source = 'tripCookie'; // Used for debugging in store.js
        return datesFromTripCookie;
    }

    let datesFromStateCookie = null;
    if (stateCookie.previousArrivalDate && stateCookie.previousDepartureDate) {
        const sArrival = formatDate(decodeURIComponent(stateCookie.previousArrivalDate));
        const sDeparture = formatDate(decodeURIComponent(stateCookie.previousDepartureDate));
        datesFromStateCookie = qualifyDates(sArrival, sDeparture);
    }

    if (datesFromStateCookie) {
        datesFromStateCookie.source = 'stayCookie'; // Used for debugging in store.js
        return datesFromStateCookie;
    }

    return emptyDates;
}

/* eslint-enable complexity */

function getIsFamilySegment(hasKids, stateCookie) {
    const isFamilyFilterSet = (
        stateCookie &&
        stateCookie.lastSearchUrl &&
        stateCookie.lastSearchUrl.indexOf('filter:43') > 0
    );
    return hasKids || isFamilyFilterSet;
}

/* eslint-disable complexity */
function determineTripData(haTripPrst, haStatePrst, query, logFn) {
    const tripDates = determineTripDates(query, haTripPrst, haStatePrst, logFn);

    // Allow sloppy query names
    const adultsFields = {
        used: null,
        notUsed: [],
        invalid: [],
        all: ['numberOfAdults', 'adultsCount', 'adults', 'guests']
    };
    const qNumberOfAdults = getValidQueryField(query, adultsFields, logFn, (value) => {
        return !isNaN(value);
    });

    const childrenFields = {
        used: null,
        notUsed: [],
        invalid: [],
        all: ['numberOfChildren', 'childrenCount', 'children']
    };
    let qNumberOfChildren = getValidQueryField(query, childrenFields, logFn, (value) => {
        return !isNaN(value);
    });

    const infantsFields = {
        used: null,
        notUsed: [],
        invalid: [],
        all: ['numberOfInfants', 'infantsCount', 'infants']
    };
    let qNumberOfInfants = getValidQueryField(query, infantsFields, logFn, (value) => {
        return !isNaN(value);
    });

    const petsFields = {
        used: null,
        notUsed: [],
        invalid: [],
        all: ['petIncluded', 'petsIncluded', 'pet', 'pets']
    };
    let qPets = getValidQueryField(query, petsFields, logFn, (value) => {
        return isTrueOrFalse(value);
    });
    if (qNumberOfAdults > 0) {
        qNumberOfChildren = qNumberOfChildren || 0;
        qNumberOfInfants = qNumberOfInfants || 0;
        qPets = qPets || false;
    }

    const safeParseAsNumber = (input) => (+input) || 0;

    let adultsCount = safeParseAsNumber(!isNaN(qNumberOfAdults) ? qNumberOfAdults : haTripPrst.numberOfAdults);

    let childrenCount = safeParseAsNumber(!isNaN(qNumberOfChildren) ? qNumberOfChildren : haTripPrst.numberOfChildren);
    let infantsCount = safeParseAsNumber(!isNaN(qNumberOfInfants) ? qNumberOfInfants : haTripPrst.numberOfInfants);
    let hasPets = (typeof qPets === 'undefined') ? (haTripPrst.petIncluded || false) : qPets;
    // At this point, hasPets could be a bool or a string.
    // if it is a string, convert it.
    /* eslint-disable no-nested-ternary */
    hasPets = (typeof hasPets === 'string') ? (hasPets.toLowerCase() === 'true') ? true : false : !!hasPets;
    /* eslint-enable no-nested-ternary */

    if (query.noGuests === 'true') {
        adultsCount = 0;
        childrenCount = 0;
        infantsCount = 0;
    }

    const isFamilySegment = getIsFamilySegment((childrenCount > 0 || infantsCount > 0), haStatePrst);

    return {
        ...tripDates,
        adultsCount,
        childrenCount,
        infantsCount,
        hasPets,
        isFamilySegment
    };
}

/* eslint-enable complexity */

function determineTripDataFromRequest(request) {
    const haTripPrst = readCookie(TRIP_COOKIE, request.headers.cookie);
    const haStatePrst = readCookie('ha-state-prst', request.headers.cookie);
    const query = request.query || {};
    const logFn = () => {
        // don't log anything when processing query params on the backend
    };

    return determineTripData(haTripPrst, haStatePrst, query, logFn);
}

function determineTripDataFromWindow(window) {
    const haTripPrst = readCookie(TRIP_COOKIE, window.document.cookie);
    const haStatePrst = readCookie('ha-state-prst', window.document.cookie);
    const query = getQueryParams(window.location.search);
    const logFn = (data) => {
        data.fromClient = true;
        logToServer(data);
    };

    return determineTripData(haTripPrst, haStatePrst, query, logFn);
}

export {determineTripData, determineTripDataFromRequest, determineTripDataFromWindow};
